<template>
    <div class="space-y-3">
        <section class="space-y-1">
            <form-label>Drives</form-label>
            <p v-if="!targets.length" class="text-xs text-gray-300">Drag a wire from this binding's output to an object.</p>
            <div v-for="target in targets" :key="target.wire.id" class="flex items-center justify-between gap-2 text-xs">
                <span class="truncate">{{ target.label }}</span>
                <button class="text-red-300 hover:text-red-200" title="Disconnect" @click="hass.removeWire(page, target.wire.id)">×</button>
            </div>
        </section>

        <form-select name="Strategy" :model-value="node.type" @update:model-value="changeStrategy">
            <option v-for="entry in strategies" :key="entry.nodeType" :value="entry.nodeType">{{ entry.label }}</option>
        </form-select>

        <form-select v-if="propertyChoices.length" name="Property" :model-value="node.data.property"
            @update:model-value="property => hass.updateNodeData(page, node.id, { property })">
            <option value="" disabled>Select a property</option>
            <option v-for="choice in propertyChoices" :key="choice.property" :value="choice.property" :disabled="choice.disabled">
                {{ choice.property }}{{ choice.disabled ? ' (bound)' : '' }}
            </option>
        </form-select>

        <section v-if="caseWire" class="space-y-1">
            <form-label>Applies in case</form-label>
            <div class="flex items-center justify-between gap-2 text-xs">
                <button class="truncate text-left hover:text-ui-active" title="Select the switch"
                    @click="hass.select(page, 'node', caseWire.from.node)">⑂ {{ caseLabel }}</button>
                <button class="text-red-300 hover:text-red-200" title="Disconnect the case" @click="hass.removeWire(page, caseWire.id)">×</button>
            </div>
        </section>

        <p v-else-if="fallbackSwitch" class="text-xs text-gray-300">
            Applies when no case of
            <button class="hover:text-ui-active" title="Select the switch" @click="hass.select(page, 'node', fallbackSwitch.id)">⑂ Switch</button>
            matches.
        </p>

        <section v-if="readsEntity" class="space-y-1">
            <form-label>Entity</form-label>
            <div v-if="entityWire" class="flex items-center justify-between gap-2 text-xs">
                <button class="truncate text-left hover:text-ui-active" title="Select the entity node"
                    @click="hass.select(page, 'node', entityWire.from.node)">{{ wiredEntityId || '(no entity selected)' }}</button>
                <button class="text-red-300 hover:text-red-200" title="Disconnect the entity" @click="hass.removeWire(page, entityWire.id)">×</button>
            </div>
            <p v-else-if="switchEntityId" class="text-xs text-gray-300">
                The switch's entity <span class="font-mono text-white">{{ switchEntityId }}</span>; wire an entity node to use another.
            </p>
            <p v-else-if="primaryEntity" class="text-xs text-gray-300">
                Primary Entity <span class="font-mono text-white">{{ primaryEntity }}</span>; wire an entity node to use another.
            </p>
            <p v-else class="text-xs text-amber-300">Wire an entity node to this binding, or give the object a Primary Entity.</p>
        </section>

        <BindingEditorRouter v-if="editorObject && node.data.property" :key="`${node.id}:${node.type}:${editorObject.id()}`"
            :binding="draft" :node="editorObject" :settings="settings" :editorState="editorState" in-flow-graph />
        <p v-else-if="!editorObject" class="text-xs text-gray-300">Wire this binding to an object to edit its value.</p>
    </div>
</template>

<script setup>
import { computed, toRaw } from 'vue';
import { useHassFlowStore } from '../../stores/hassFlowStore.js';
import { BINDING_NODE_TYPES, getHassNodePort, getHassPalette } from '../../Hasp/properties/v2/hass/index.js';
import { findNode } from '../../graph/graphModel.js';
import { getBindingPropertyChoices, getBindingTargets, getSwitchEntityId, getSwitchFallbackBindings, getWiresInto } from '../../hass/hassGraph.js';
import { getEffectiveHassConfig } from '../../hass/hassEffective.js';
import { getEffectivePrimaryEntity } from '../../automation/primaryEntity.js';
import { AutomationBinding } from '../../automation/ast/AutomationBinding.js';
import { createBindingEditorStateManager } from '../hassbuilder/automation/BindingEditorStateManager.js';
import BindingEditorRouter from '../hassbuilder/automation/BindingEditorRouter.vue';
import { useNodeDraft } from './useNodeDraft.js';

/**
 * A binding node: the objects it drives, its strategy and property, the
 * entity it reads, and the HASSBuilder's binding editor for the rest
 * (transforms, formatter, mapping rules, template, constant value).
 */
const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    context: { type: Object, required: true },
    settings: { type: Object, default: () => ({}) },
});
const hass = useHassFlowStore();

const strategies = getHassPalette().find(group => group.id === 'bindings').entries.filter(entry => BINDING_NODE_TYPES[entry.nodeType]);
function changeStrategy(type) {
    const entry = strategies.find(item => item.nodeType === type);
    if (entry && type !== props.node.type) hass.setNodeVariant(props.page, props.node.id, entry, { property: props.node.data.property });
}

const targets = computed(() => props.graph.wires
    .filter(wire => wire.from.node === props.node.id && wire.to.object !== undefined)
    .map(wire => {
        const object = props.context.getObject(wire.to.object);
        return { wire, object, label: object ? `${object.obj} p${props.page.haspid}b${object.haspid}` : 'missing object' };
    }));
// The binding editor works on the first object it drives (property metadata, previews).
const editorObject = computed(() => getBindingTargets(props.graph, props.node.id)
    .map(uuid => props.context.getObject(uuid)).find(Boolean) ?? null);
const propertyChoices = computed(() => getBindingPropertyChoices(props.graph, props.node.id, props.context));

const readsEntity = computed(() => ['state', 'mapping'].includes(props.node.data.strategy));
const entityWire = computed(() => getWiresInto(props.graph, { node: props.node.id, port: 'entity' })[0] ?? null);
const wiredEntityId = computed(() => (entityWire.value ? findNode(props.graph, entityWire.value.from.node)?.data.entityId ?? '' : ''));
const caseWire = computed(() => getWiresInto(props.graph, { node: props.node.id, port: 'case' })[0] ?? null);
const caseLabel = computed(() => {
    const port = caseWire.value && getHassNodePort(findNode(props.graph, caseWire.value.from.node), 'out', caseWire.value.from.port);
    return port?.label ?? '';
});
const switchEntityId = computed(() => (caseWire.value ? getSwitchEntityId(props.graph, caseWire.value.from.node) : ''));
const fallbackSwitch = computed(() => props.graph.nodes.find(item => item.kind === 'switch'
    && getSwitchFallbackBindings(props.graph, item.id).some(binding => binding.id === props.node.id)) ?? null);
// page.hass is plain data: depend on the graph prop so a Primary Entity wire change is seen.
const primaryEntity = computed(() => (props.graph && editorObject.value
    ? getEffectivePrimaryEntity(editorObject.value, { configOf: getEffectiveHassConfig }) : ''));

// The editors mutate an AutomationBinding in place (see useNodeDraft). Its
// entity is the effective one, for previews only: the graph owns it.
const editorState = createBindingEditorStateManager().get(props.node.id);
const draft = useNodeDraft({
    build: () => AutomationBinding.fromJSON({
        ...structuredClone(toRaw(props.node).data),
        id: props.node.id,
        entityId: wiredEntityId.value || props.node.data.entityId || switchEntityId.value || primaryEntity.value || '',
    }),
    snapshot: binding => {
        const { id, entityId, ...data } = binding.toJSON();
        // A legacy entity kept in data (a template source) is the graph's, not the editor's.
        if (props.node.data.entityId !== undefined) data.entityId = props.node.data.entityId;
        return data;
    },
    current: () => props.node.data,
    commit: data => hass.setNodeData(props.page, props.node.id, data),
    rebuildOn: () => [wiredEntityId.value, switchEntityId.value, primaryEntity.value],
});
</script>
